import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ReimbursementCampusModel,
  ReimbursementManagementFilters,
  ReimbursementModel,
  ReimbursementSubmissionModel,
  ReimbursementTypeModel
} from 'src/app/models/reimbursementModel';
import { PayPeriodModel } from 'src/app/models/payPeriodModel';

@Injectable({
  providedIn: 'root'
})
export class ReimbursementService {
  constructor(private http: HttpClient) {
  }

  getMyReimbursements(): Observable<ReimbursementModel[]> {
    return this.http.get<ReimbursementModel[]>(`${environment.apiServer.webApiURL}/Reimbursement/My`);
  }

  getActiveTypes(): Observable<ReimbursementTypeModel[]> {
    return this.http.get<ReimbursementTypeModel[]>(`${environment.apiServer.webApiURL}/Reimbursement/Types`);
  }

  getManagementReimbursements(filters: ReimbursementManagementFilters): Observable<ReimbursementModel[]> {
    let params = new HttpParams();
    Object.keys(filters).forEach(key => {
      const value = filters[key];
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, value.toString());
      }
    });
    return this.http.get<ReimbursementModel[]>(
      `${environment.apiServer.webApiURL}/Reimbursement/Management`,
      { params });
  }

  getManagementCampuses(): Observable<ReimbursementCampusModel[]> {
    return this.http.get<ReimbursementCampusModel[]>(
      `${environment.apiServer.webApiURL}/Reimbursement/Management/Campuses`);
  }

  getManagementTypes(): Observable<ReimbursementTypeModel[]> {
    return this.http.get<ReimbursementTypeModel[]>(
      `${environment.apiServer.webApiURL}/Reimbursement/Management/Types`);
  }

  getManagementPayPeriods(): Observable<PayPeriodModel[]> {
    return this.http.get<PayPeriodModel[]>(
      `${environment.apiServer.webApiURL}/Reimbursement/Management/PayPeriods`);
  }

  getAllTypes(): Observable<ReimbursementTypeModel[]> {
    return this.http.get<ReimbursementTypeModel[]>(
      `${environment.apiServer.webApiURL}/Reimbursement/TypeManagement`);
  }

  addType(type: ReimbursementTypeModel): Observable<ReimbursementTypeModel> {
    return this.http.post<ReimbursementTypeModel>(
      `${environment.apiServer.webApiURL}/Reimbursement/TypeManagement`, type);
  }

  updateType(type: ReimbursementTypeModel): Observable<ReimbursementTypeModel> {
    return this.http.put<ReimbursementTypeModel>(
      `${environment.apiServer.webApiURL}/Reimbursement/TypeManagement/${type.id}`, type);
  }

  submit(input: ReimbursementSubmissionModel): Observable<ReimbursementModel> {
    return this.http.post<ReimbursementModel>(
      `${environment.apiServer.webApiURL}/Reimbursement`,
      this.toFormData(input),
      { headers: new HttpHeaders() });
  }

  resubmit(id: number, input: ReimbursementSubmissionModel): Observable<ReimbursementModel> {
    return this.http.put<ReimbursementModel>(
      `${environment.apiServer.webApiURL}/Reimbursement/${id}/Resubmit`,
      this.toFormData(input),
      { headers: new HttpHeaders() });
  }

  approve(id: number, approvedAmount: number, payPeriodID: number): Observable<ReimbursementModel> {
    return this.http.post<ReimbursementModel>(
      `${environment.apiServer.webApiURL}/Reimbursement/${id}/Approve`,
      { approvedAmount, payPeriodID });
  }

  deny(id: number, denialReason: string): Observable<ReimbursementModel> {
    return this.http.post<ReimbursementModel>(
      `${environment.apiServer.webApiURL}/Reimbursement/${id}/Deny`,
      { denialReason });
  }

  downloadReceipt(attachmentID: number): Observable<ArrayBuffer> {
    return this.http.get<ArrayBuffer>(
      `${environment.apiServer.webApiURL}/Reimbursement/Attachments/${attachmentID}`,
      { responseType: 'arraybuffer' as 'json' });
  }

  private toFormData(input: ReimbursementSubmissionModel): FormData {
    const formData = new FormData();
    formData.append('ReimbursementTypeID', input.reimbursementTypeID.toString());
    formData.append('ExpenseDate', input.expenseDate);
    formData.append('Merchant', input.merchant);
    formData.append('Description', input.description);
    formData.append('RequestedAmount', input.requestedAmount.toString());
    formData.append('Confirmed', input.confirmed.toString());
    if (input.receipt) {
      formData.append('Receipt', input.receipt, input.receipt.name);
    }
    return formData;
  }
}
